/**
 * Client vs owner experience lane helpers.
 */

import {
  ROLE_USER,
  resolveEffectiveRole,
} from './roleContext';

export const QA_VIEW_AS_WARNING_TEST_ID = 'qa-view-as-warning';

export function shouldShowQaViewAsWarning({ isAdmin, viewAsRole }) {
  return Boolean(isAdmin && viewAsRole);
}

/**
 * Inline header quick links for client/guest experience (not buried in dropdown).
 */
export function showsClientHeaderQuickLinks({ user, isAdmin, viewAsRole, isGuestContext: guestCtx }) {
  if (!user) {
    return false;
  }
  if (isAdmin && !viewAsRole) {
    return false;
  }
  if (guestCtx) {
    return true;
  }
  return resolveEffectiveRole({ user, isAdmin, viewAsRole }) === ROLE_USER;
}

/**
 * Client dashboard is available to any signed-in user browsing as a client,
 * including owners who also act as renters.
 */
export function canAccessClientDashboard({ user, isAdmin, viewAsRole }) {
  if (!user) {
    return false;
  }
  if (isAdmin && !viewAsRole) {
    return false;
  }
  return true;
}

export function isPureClientAccount({ user, isAdmin, viewAsRole }) {
  if (!user || isAdmin) {
    return false;
  }
  return String(user.role || '').toUpperCase() === ROLE_USER;
}

export function showsOwnerDashboardNav({ canAccessOwner, isGuestContext: guestCtx }) {
  return Boolean(canAccessOwner && !guestCtx);
}

export function resolveClientHeaderQuickLinks() {
  return [
    {
      href: '/listings',
      labelKey: 'client.nav_browse_homes',
      defaultLabel: 'Browse Homes',
      testId: 'client-nav-browse',
    },
    {
      href: '/dashboard',
      labelKey: 'nav.client_home_search',
      defaultLabel: 'My Home Search',
      testId: 'client-nav-dashboard',
    },
    {
      href: '/notifications',
      labelKey: 'nav.inbox',
      defaultLabel: 'Inbox',
      testId: 'client-nav-inbox',
    },
  ];
}

/** Default public nav for owner/admin/non-client header mode. */
export function resolveDefaultPublicNavLinks() {
  return [
    {
      href: '/listings',
      labelKey: 'nav.listings',
      defaultLabel: 'Browse',
      testId: 'nav-browse-homes',
    },
    {
      href: '/locations',
      labelKey: 'nav.locations',
      defaultLabel: 'Locations',
      testId: 'nav-locations',
    },
    {
      href: '/concierge',
      labelKey: 'nav.concierge',
      defaultLabel: 'PCS Buddy',
      testId: 'nav-concierge',
    },
  ];
}

/**
 * Single header nav source — client mode replaces public nav to avoid duplicate Browse links.
 */
export function resolveHeaderNavLinks({ showsClientHeaderQuickLinks }) {
  if (showsClientHeaderQuickLinks) {
    return resolveClientHeaderQuickLinks();
  }
  return resolveDefaultPublicNavLinks();
}

export function headerNavHasDuplicateHrefs(links) {
  const hrefs = links.map((link) => link.href);
  return hrefs.length !== new Set(hrefs).size;
}
